import React from 'react'
import App from './App'
import { store } from './app/store'
import { Provider } from 'react-redux'
import * as serviceWorker from './serviceWorker'
import { AppLocalizationProvider } from './app/L10n'
import {
  createTheme,
  CssBaseline,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { enableMapSet } from 'immer'
import plLocale from 'date-fns/locale/pl'
import deLocale from 'date-fns/locale/de'
import enLocale from 'date-fns/locale/en-US'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { createRoot } from 'react-dom/client'
import "./styles.css";
import { useAppSelector } from './app/hooks'
import { selectSettings } from './features/settings/settingsSlice'

const AppDateLocalizationProvider = ({ children }: { children: React.ReactNode }) => {
  const { locale } = useAppSelector(selectSettings)
  const adapterLocale = locale === 'pl' ? plLocale : locale === 'de' ? deLocale : enLocale
  return <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={adapterLocale}>{children}</LocalizationProvider>
}

export const toolbarMinHeight = 64

const theme = createTheme({
  palette: {
    primary: {
      main: 'rgba(65, 85, 99, 1)',
      contrastText: '#fff',
    },
    secondary: {
      main: 'rgba(71, 184, 172, 1)',
      contrastText: '#fff'
    },
  },
  mixins: {
    toolbar: {
      minHeight: toolbarMinHeight,
    }
  },
  components: {
    MuiDataGrid: {
      defaultProps: {
        columnHeaderHeight: 40,
      },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f3f4f6',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#f3f4f6',
            paddingTop: '0.1rem',
            paddingBottom: '0.1rem',
          },
          '& .MuiDataGrid-main': {
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-virtualScroller': {
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-overlayWrapper': {
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-filler': {
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-scrollbarFiller': {
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-scrollbarFiller--header': {
            backgroundColor: '#f3f4f6',
          },
          '& .MuiDataGrid-scrollbarFiller--pinnedRight': {
            backgroundColor: '#ffffff',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#f2f2f2',
          },
          marginTop: '5px',
          height: '40px',
          minHeight: '40px',
          maxHeight: '40px',
        },
      },

    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.grey[200],
          fontWeight: theme.typography.fontWeightBold,
          padding: theme.spacing(1, 2),
        }),
      },
    },
  }
})

enableMapSet()

const container = document.getElementById('root')
const root = createRoot(container!)

root.render(
  <React.StrictMode>
    <CssBaseline />
    <Provider store={store}>
      <AppLocalizationProvider>
        <AppDateLocalizationProvider>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <App />
            </ThemeProvider>
          </StyledEngineProvider>
        </AppDateLocalizationProvider>
      </AppLocalizationProvider>
    </Provider>
  </React.StrictMode>
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
